import React, { useState, useEffect, useRef } from 'react';
import { tagsApi } from '../../api/tags';
import { Plus, X, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

export default function TagsWidget({ recordId, category }) {
  const [recordTags, setRecordTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [loading, setLoading] = useState(true);
  const pickerRef = useRef(null);

  useEffect(() => {
    Promise.all([
      tagsApi.getForRecord(recordId),
      tagsApi.list(category),
    ]).then(([recData, allData]) => {
      setRecordTags(recData.tags || []);
      setAllTags(allData.tags || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [recordId]);

  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowPicker(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addTag = async (tag) => {
    try {
      await tagsApi.addToRecord(recordId, tag.id);
      setRecordTags(prev => [...prev, tag]);
    } catch (err) { toast.error('Failed to add tag'); }
  };

  const removeTag = async (tag) => {
    try {
      await tagsApi.removeFromRecord(recordId, tag.id);
      setRecordTags(prev => prev.filter(t => t.id !== tag.id));
    } catch (err) { toast.error('Failed to remove tag'); }
  };

  const createAndAdd = async () => {
    if (!newTagName.trim()) return;
    try {
      const { tag } = await tagsApi.create({ name: newTagName.trim(), category: category || 'general' });
      setAllTags(prev => [...prev, tag]);
      await addTag(tag);
      setNewTagName('');
    } catch (err) {
      if (err.response?.status === 409) toast.error('Tag already exists');
      else toast.error('Failed to create tag');
    }
  };

  const availableTags = allTags.filter(t => !recordTags.find(rt => rt.id === t.id));

  if (loading) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {recordTags.map(tag => (
        <span key={tag.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium group"
          style={{ backgroundColor: tag.color + '20', color: tag.color }}>
          {tag.name}
          <button onClick={() => removeTag(tag)}
            className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500">
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      <div className="relative" ref={pickerRef}>
        <button onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-gray-400 border border-dashed border-gray-300 hover:border-gray-400 hover:text-gray-500 transition-colors">
          <Plus className="h-3 w-3" /> Tag
        </button>

        {showPicker && (
          <div className="absolute z-50 mt-1 left-0 w-52 bg-white rounded-lg border border-gray-200 shadow-xl">
            <div className="max-h-[200px] overflow-y-auto">
              {availableTags.length === 0 && (
                <p className="text-xs text-gray-400 p-3 text-center">No more tags available</p>
              )}
              {availableTags.map(tag => (
                <button key={tag.id} onClick={() => { addTag(tag); setShowPicker(false); }}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-xs">
                  <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                  {tag.name}
                </button>
              ))}
            </div>
            <div className="border-t border-gray-100 p-2">
              <div className="flex gap-1">
                <input type="text" value={newTagName} onChange={e => setNewTagName(e.target.value)}
                  placeholder="New tag..."
                  onKeyDown={e => e.key === 'Enter' && createAndAdd()}
                  className="flex-1 text-xs px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-brand-500" />
                <button onClick={createAndAdd} disabled={!newTagName.trim()}
                  className="px-2 py-1.5 bg-brand-600 text-white text-xs rounded hover:bg-brand-700 disabled:opacity-30">
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
